import {evaluateIndicatorSignal,signalRule} from './indicator-signals.mjs';
import {indicatorPlots} from './indicator-plots.mjs';
// Interpret actual visible indicator outputs on the latest completed candle.
// Scores describe observations, never execution instructions or probabilities.
const identities = new WeakMap();
let nextIdentity = 0;
function identity(overlay) {
    if (overlay.settings?.$uuid || overlay.id) return overlay.settings?.$uuid || overlay.id;
    if (!identities.has(overlay)) identities.set(overlay, 'indicator-' + (++nextIdentity));
    return identities.get(overlay);
}
const finite = value => typeof value === 'number' && Number.isFinite(value);
const fmt = value => Number(value).toLocaleString(undefined, {maximumFractionDigits: 6});
function atOrBefore(rows, time) {
    let low=0, high=rows.length-1, found=-1;
    while(low<=high) {const middle=(low+high)>>1;if(rows[middle]?.[0]<=time){found=middle;low=middle+1}else high=middle-1}
    return found;
}
export function indicatorInsights(source={}, candles=[], interval=300000, now=Date.now()) {
    const index=atOrBefore(candles,now-Math.max(1,interval));
    if(index<0)return {time:null, rows:[]};
    const candle=candles[index],time=candle[0];
    const overlays=[...(source?.onchart || []),...(source?.offchart || [])];
    const rows=[];
    overlays.forEach(overlay=>{
        if(overlay.settings?.candleScriptId && overlay.settings?.legend===false)return;
        if(overlay.settings?.display===false || overlay.settings?.$state || overlay.settings?.$workspaceComparison || /Tool|^System:|^Cursor$|^TimeframeTrend$/.test(overlay.type || ''))return;
        const type=String(overlay.type||''), name=overlay.name||type, series=overlay.signalData||overlay.data||[];
        const found=atOrBefore(series,time), values=series[found], previous=series[found-1];
        const row={id:identity(overlay),name,type,score:0,family:'context',status:'Context',detail:'',time};
        rows.push(row);
        if(overlay.loading){row.status='Calculating';row.detail='Waiting for this indicator to finish calculating.';return}
        if(signalRule(type)==='context' && (!overlay.settings?.signalConfig?.kind || overlay.settings.signalConfig.kind==='auto')) {
            Object.assign(row,evaluateIndicatorSignal(type,values,previous,candle,candles[index-1],overlay.settings,series.slice(0,found+1)));return;
        }
        if(signalRule(type)==='candle' && (!overlay.settings?.signalConfig?.kind || overlay.settings.signalConfig.kind==='auto')) {
            Object.assign(row,evaluateIndicatorSignal(type,values,previous,candle,candles[index-1],overlay.settings,series.slice(0,found+1)));return;
        }
        if(!values || values[0]!==time || !values.slice(1).some(finite)){row.status='Unavailable';row.detail='No finite output for the latest completed candle. Warm-up or recalculation may be required.';return}
        const outputs=(overlay.settings?.signalOutputs||indicatorPlots(overlay)).map((plot,i)=>finite(values[i+1])?(plot.label||plot.key||'Output '+(i+1))+': '+fmt(values[i+1]):null).filter(Boolean).join(' · ');
        Object.assign(row,evaluateIndicatorSignal(type,values,previous?.[0]===candles[index-1]?.[0]?previous:null,candle,candles[index-1],overlay.settings,series.slice(0,found+1)));
        if(signalRule(type)==='price' && (!overlay.settings?.signalConfig?.kind || overlay.settings.signalConfig.kind==='auto') && previous?.[0]===candles[index-1]?.[0] && finite(previous[1]) && finite(candles[index-1]?.[4])) {
            const priorClose=candles[index-1][4];
            if(priorClose<=previous[1]&&candle[4]>values[1])row.detail+=' Price crossed above on this completed candle.';
            if(priorClose>=previous[1]&&candle[4]<values[1])row.detail+=' Price crossed below on this completed candle.';
        }
        if(row.extreme) row.detail+=` ${row.extreme}; an extreme can persist and does not confirm a reversal.`;
        if(row.basis) row.detail+=` Basis: ${row.basis}.`;
        row.detail+=outputs?' Outputs: '+outputs+'.':'';
    });
    return {time,rows};
}
export function collectiveInsights(rows=[]) {
    const groups=new Map();
    for(const row of rows)if(row.score){const values=groups.get(row.family)||[];values.push(row.score);groups.set(row.family,values)}
    const votes=[...groups.values()].map(values=>values.every(value=>value>0)?1:values.every(value=>value<0)?-1:0);
    const bullish=votes.filter(v=>v>0).length,bearish=votes.filter(v=>v<0).length;
    const label=!votes.length?'No directional confirmation':bullish&&bearish||votes.includes(0)?'Mixed signals':votes.length<2?'Limited confirmation':bullish?'Bullish agreement':'Bearish agreement';
    return {label,bullish,bearish,families:votes.length,covered:rows.filter(row=>row.score).length,total:rows.length,
        detail:`${bullish} bullish and ${bearish} bearish indicator families. Related indicators count as one family; context-only and unavailable outputs do not vote. Agreement is not a forecast or a probability.`};
}
